<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  stripInlineBase64Images,
  isHtmlSignature,
} from 'dashboard/helper/editorHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateSignature']);

const { t } = useI18n();
const signature = ref(props.messageSignature ?? '');
// A signature that starts with an HTML tag is treated as raw HTML (custom tables,
// inline CSS, avatars) and rendered as-is on outgoing email instead of being run
// through the markdown pipeline, which can't express that kind of styling.
// The parent loads the current user asynchronously, so messageSignature often
// arrives after this component has already mounted with the '' default - track
// mode reactively (with immediate:true) instead of computing it once at setup.
const isHtmlMode = ref(isHtmlSignature(props.messageSignature));
const modeManuallySet = ref(false);
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
    if (!modeManuallySet.value) {
      isHtmlMode.value = isHtmlSignature(newValue);
    }
  },
  { immediate: true }
);

const isHtmlContent = computed(() => isHtmlSignature(signature.value));

const toggleHtmlMode = () => {
  modeManuallySet.value = true;
  isHtmlMode.value = !isHtmlMode.value;
};

const updateSignature = () => {
  if (isHtmlMode.value) {
    signature.value = (signature.value || '').trim();
    emit('updateSignature', signature.value);
    return;
  }

  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INLINE_IMAGE_WARNING')
    );
  }
  emit('updateSignature', signature.value);
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateSignature()">
    <WootMessageEditor
      v-if="!isHtmlMode"
      id="message-signature-input"
      v-model="signature"
      class="message-editor h-[10rem] !px-3"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
      show-image-resize-toolbar
    />
    <textarea
      v-else
      id="message-signature-input"
      v-model="signature"
      rows="8"
      class="w-full font-mono text-xs rounded-lg border border-n-weak p-3 bg-transparent"
      :placeholder="
        $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.HTML_PLACEHOLDER')
      "
    />
    <div
      v-if="isHtmlMode && isHtmlContent"
      class="border border-dashed border-n-weak rounded-lg p-3"
    >
      <p class="text-xs text-n-slate-11 mb-2">
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW') }}
      </p>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-html="signature" />
    </div>
    <div class="flex items-center gap-3">
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
      />
      <NextButton
        type="button"
        link
        slate
        :label="
          isHtmlMode
            ? $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SWITCH_TO_RICH_TEXT'
              )
            : $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.SWITCH_TO_HTML'
              )
        "
        @click="toggleHtmlMode"
      />
    </div>
  </form>
</template>
